// /opt/isp_erp/frontend/src/components/tickets/ReassignTicketDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Box,
  Typography,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import { Person, CheckCircle, Cancel, Warning } from '@mui/icons-material';
import axios from '../../api/axios';

const getEstadoColor = (estado) => {
  switch (estado) {
    case 'DISPONIBLE': return 'success';
    case 'PERMISO_APROBADO': return 'success';
    case 'OCUPADO': return 'warning';
    case 'PERMISO_SOLICITADO': return 'info';
    case 'PERMISO_RECHAZADO': return 'error';
    default: return 'default';
  }
};

const getEstadoLabel = (estado) => {
  switch (estado) {
    case 'DISPONIBLE': return '🟢 Disponible';
    case 'PERMISO_APROBADO': return '🟢 Permiso Aprobado';
    case 'OCUPADO': return '🟡 Ocupado';
    case 'PERMISO_SOLICITADO': return '🔵 Permiso Solicitado';
    case 'PERMISO_RECHAZADO': return '🔴 Permiso Rechazado';
    default: return estado || 'Desconocido';
  }
};

export default function ReassignTicketDialog({ open, onClose, ticket, fechaTicket, onSuccess }) {
  const [tecnicos, setTecnicos] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cargandoTecnicos, setCargandoTecnicos] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && ticket) {
      loadTecnicosDisponibles();
    }
  }, [open, ticket]);

  const loadTecnicosDisponibles = async () => {
    setCargandoTecnicos(true);
    setError('');
    try {
      let url = `/tickets/${ticket.id}/tecnicos-disponibles`;
      
      // ✅ Si hay una fecha específica, usar el endpoint con fecha
      if (fechaTicket) {
        const fechaStr = new Date(fechaTicket).toISOString().split('T')[0];
        url = `/tickets/tecnicos-disponibles-por-fecha?fecha=${fechaStr}`;
      }
      
      const response = await axios.get(url);
      setTecnicos(response.data);
      if (response.data.length === 0) {
        setError('No hay técnicos disponibles para esta fecha');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar técnicos disponibles');
    } finally {
      setCargandoTecnicos(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTechnician) {
      setError('Debe seleccionar un técnico');
      return;
    }
    if (!motivo || motivo.trim() === '') {
      setError('Debe ingresar un motivo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.put(`/tickets/${ticket.id}/reasignar`, null, {
        params: {
          nuevo_tecnico_id: selectedTechnician,
          motivo: motivo
        }
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al reasignar ticket');
    } finally {
      setLoading(false);
    }
  };

  const selectedTechInfo = tecnicos.find(t => t.id === selectedTechnician);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Reasignar Ticket
        <Typography variant="body2" color="text.secondary">
          Ticket: {ticket?.ticket_number} - {ticket?.title}
        </Typography>
        {ticket?.assigned_technician_name && (
          <Typography variant="caption" color="text.secondary" display="block">
            Técnico actual: {ticket.assigned_technician_name}
          </Typography>
        )}
        {fechaTicket && (
          <Typography variant="caption" color="text.secondary" display="block">
            Fecha del ticket: {new Date(fechaTicket).toLocaleDateString()}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity={error.includes('No hay técnicos') ? 'warning' : 'error'} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {cargandoTecnicos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : tecnicos.length > 0 ? (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Seleccionar Nuevo Técnico</InputLabel>
                <Select
                  value={selectedTechnician}
                  onChange={(e) => setSelectedTechnician(e.target.value)}
                  label="Seleccionar Nuevo Técnico"
                >
                  {tecnicos.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Person fontSize="small" />
                        <Typography sx={{ flex: 1 }}>{tech.username}</Typography>
                        <Chip
                          label={getEstadoLabel(tech.disponibilidad_estado)}
                          color={getEstadoColor(tech.disponibilidad_estado)}
                          size="small"
                        />
                        <Chip
                          label={`${tech.tickets_activos || 0} activos`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedTechInfo && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info">
                    <strong>Técnico seleccionado:</strong> {selectedTechInfo.username}
                    {selectedTechInfo.disponibilidad_motivo && (
                      <span> - Motivo: {selectedTechInfo.disponibilidad_motivo}</span>
                    )}
                    {selectedTechInfo.tickets_activos > 3 && (
                      <span style={{ display: 'block', color: '#856404', marginTop: '4px' }}>
                        ⚠️ Este técnico tiene {selectedTechInfo.tickets_activos} tickets activos.
                      </span>
                    )}
                  </Alert>
                </Box>
              )}

              <TextField
                fullWidth
                label="Motivo de reasignación"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                multiline
                rows={3}
                placeholder="Ej: El técnico actual no está disponible, Reasignación por prioridad, etc."
                required
              />
            </>
          ) : (
            !cargandoTecnicos && (
              <Alert severity="warning">
                No hay técnicos disponibles para reasignar.
                Por favor, cambia la disponibilidad de algún técnico o espera a que estén disponibles.
              </Alert>
            )
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={loading || !selectedTechnician || !motivo || tecnicos.length === 0}
        >
          {loading ? <CircularProgress size={24} /> : 'Reasignar Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
